<template>
  <div ref="root">
    <div>
      <GhostButton
        class="tw:flex tw:items-center tw:gap-2"
        @click="triggerFlyout"
        :class="{ 'btn-active': isFlyoutFiltersOpen }"
      >
        <FiltersIcon class="filter tw:fill-gray-800 tw:size-[18px]" />
        {{ $t('filters') }}
        <span
          v-if="selectedFiltersAmount > 0"
          class="filter-badge tw:bg-blue-700 tw:text-white tw:rounded-full tw:w-[20px] tw:h-[20px] tw:text-sm"
        >
          {{ selectedFiltersAmount }}
        </span>
      </GhostButton>
    </div>
    <div class="tw:relative">
      <div
        v-if="isFlyoutFiltersOpen"
        class="tw:absolute tw:top-[6px] tw:z-39 modal-filters tw:mx-auto tw:w-full tw:sm:min-w-[480px] tw:md:min-w-[740px] tw:landing:max-h-[500px] tw:bg-white tw:m-auto tw:rounded-lg shadow"
      >
        <div class="tw:relative tw:flex tw:flex-row tw:mt-0 tw:min-h-[500px]">
          <div class="tw:flex tw:border-r tw:border-gray-300 tw:p-5 tw:min-w-[260px] tw:flex-col tw:max-h-[500px]">
            <button
              v-for="filter in currentFilters"
              :key="filter.value"
              class="tw:flex tw:w-full tw:font-bold tw:px-4 tw:py-3 tw:mb-1 tw:rounded-lg tw:text-left"
              :class="getItemClass(filter.value)"
              @click="scrollToElement(filter.value)"
            >
              {{ $t(filter.label) }}
            </button>
          </div>

          <div
            ref="scrollContainer"
            class="tw:flex tw:flex-col tw:px-8 tw:h-full tw:h-[calc(100vh-150px)] tw:w-full tw:max-h-[420px] tw:landing:h-[450px] tw:overflow-auto"
          >
            <MobileSelectTimeframeFilter
              type="flyout"
              ref="timeframe"
              name="timeframes"
              :label="$t('tbs_invoice_timeframe')"
              :options="timeframeOptions"
              :value="temporaryFiltersObject['timeframe']"
              :startDate="temporaryFiltersObject['startDate']"
              :endDate="temporaryFiltersObject['endDate']"
              @onSelect="onSelectTempTimeframe"
              @onStartDateSelect="onSelectTempCustomStartDate"
              @onEndDateSelect="onSelectTempCustomEndDate"
              class="tw:pt-6"
              @mouseover="hoveredElement = 'timeframe'"
              @mouseleave="hoveredElement = null"
            />
            <div class="tw:border-t tw:border-gray-200 tw:h-[1px] tw:w-full tw:my-3" />
            <MobileSelectProductFilter
              type="flyout"
              ref="products"
              name="products"
              :label="$t('tbs_product_filter')"
              :options="productsOptions"
              :value="temporaryFiltersObject['products']"
              @onSelect="onSelectTempProducts"
              @mouseover="hoveredElement = 'products'"
              @mouseleave="hoveredElement = null"
            />
            <div v-if="hasPropertiesFilter" class="tw:border-t tw:border-gray-200 tw:h-[1px] tw:w-full tw:my-3" />
            <MobileSelectPropertyFilter
              v-if="hasPropertiesFilter"
              type="flyout"
              ref="properties"
              :label="$t('hotels')"
              :options="propertiesOptions"
              name="properties"
              :value="temporaryFiltersObject['properties']"
              @onSelect="onSelectTempPropertyFilters"
              @mouseover="hoveredElement = 'properties'"
              @mouseleave="hoveredElement = null"
            />
            <div class="tw:border-t tw:border-gray-200 tw:h-[1px] tw:w-full tw:my-3" />
            <MobileSelectStatusFilter
              type="mobile"
              ref="status"
              name="status"
              :label="$t('tbs_invoice_status')"
              :options="statusOptions"
              :value="temporaryFiltersObject['status']"
              @onSelect="onSelectTempStatus"
              @mouseover="hoveredElement = 'status'"
              @mouseleave="hoveredElement = null"
            />
          </div>
        </div>

        <div
          class="footer shadow tw:absolute tw:bottom-0 tw:w-full tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-4 tw:px-8 tw:text-gray-800 tw:bg-white tw:rounded-b-lg"
        >
          <div class="tw:flex tw:w-full tw:justify-between tw:items-center">
            <div>
              <button class="tw:disabled:text-gray-400 tw:font-bold" @click="resetTempFilters" :disabled="!hasChanges">
                {{ $t('rc_filter_reset') }}
              </button>
            </div>
            <MainButton size="medium" @click="applyFilters" :disabled="!hasChanges">{{ $t('apply') }}</MainButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onDeactivated, onMounted, ref, watchEffect } from 'vue'

import MainButton from '@/components/base/buttons/MainButton.vue'
import MobileSelectTimeframeFilter from '@/components/billing/invoices/filters/MobileSelectTimeframeFilter.vue'
import MobileSelectProductFilter from '@/components/billing/invoices/filters/MobileSelectProductFilter.vue'
import MobileSelectStatusFilter from '@/components/billing/invoices/filters/MobileSelectStatusFilter.vue'
import MobileSelectPropertyFilter from '@/components/billing/premiumProducts/filters/MobileSelectPropertyFilter.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import FiltersIcon from '@/components/icons/FiltersIcon.svg?component'

import { useInvoicesFilters } from '@/components/billing/invoices/useInvoicesFilters'
import { cloneDeep, isEqual } from '@/utils/Utility'

const {
  isFlyoutFiltersOpen,
  closeFlyoutFilters,
  openFlyoutFilters,
  startDate,
  endDate,
  productTypeOptions,
  selectedFiltersAmount,
} = useInvoicesFilters()

const props = defineProps({
  currentFilters: Array,
  mobileFiltersOpen: Boolean,
  timeframeOptions: Array,
  timeframeValue: Object,
  productsOptions: Array,
  productsValue: Array,
  statusOptions: Array,
  statusValues: Array,
  propertiesOptions: Array,
  propertiesValues: Array,
  hasPropertiesFilter: Boolean,
})

const root = ref(null)
const scrollContainer = ref(null)
const emit = defineEmits(['applyFilters'])
const hoveredElement = ref(null)
const activeItem = ref(props.currentFilters[0].value)

const getItemClass = item => {
  if (item === activeItem.value) return 'tw:bg-blue-200 tw:text-blue-700'
  else if (item === hoveredElement.value) return 'tw:bg-gray-200 tw:text-gray-800'
  else 'tw:hover:bg-gray-200 tw:hover:text-gray-800 tw:text-gray-600'
}

const closeFilters = () => {
  closeFlyoutFilters()
  activeItem.value = props.currentFilters[0].value
}

const triggerFlyout = () => {
  if (isFlyoutFiltersOpen.value) {
    closeFlyoutFilters()
  } else {
    openFlyoutFilters()
  }
}

const temporaryFiltersObject = ref({})
const applyFilters = () => emit('applyFilters', changes.value, false, 'flyout')

const initialFilters = ref({
  timeframe: { id: 'last-6-months', label: 'last_6_months', value: '6' },
  startDate: null,
  endDate: null,
  products: productTypeOptions.value,
  properties: [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }],
  status: [
    { id: 'payment-due', label: 'tbs_invoice_payment_due', value: 'PAYMENT_DUE' },
    { id: 'overdue', label: 'tbs_invoice_payment_overdue', value: 'OVERDUE' },
    { id: 'paid', label: 'tbs_invoice_payment_paid', value: 'PAID' },
  ],
})

const resetTempFilters = () => {
  const keys = Object.keys(changes.value)
  const result = Object.fromEntries(keys.map(key => [key, initialFilters.value[key]]))
  emit('applyFilters', result, true, 'flyout')

  temporaryFiltersObject.value = cloneDeep(initialFilters.value)
  selectedFiltersAmount.value = 0
}

const toYearMonth = dateString => {
  return dateString.split('-').slice(0, 2).join('-')
}

temporaryFiltersObject.value['timeframe'] = cloneDeep(props.timeframeValue)
temporaryFiltersObject.value['startDate'] = startDate.value !== null ? cloneDeep(toYearMonth(startDate.value)) : null
temporaryFiltersObject.value['endDate'] = endDate.value !== null ? cloneDeep(toYearMonth(endDate.value)) : null
temporaryFiltersObject.value['status'] = cloneDeep(props.statusValues)
temporaryFiltersObject.value['properties'] = cloneDeep(props.propertiesValues)

// Products Value take a bit more time to setup because it is based on PRO subscriptions
watchEffect(() => {
  if (props.productsValue) {
    temporaryFiltersObject.value['products'] = cloneDeep(props.productsValue)
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onDeactivated(() => {
  document.removeEventListener('click', handleClickOutside)
})

const handleClickOutside = evt => {
  if (root?.value !== null && !root?.value?.contains(evt.target)) {
    onClickAwayFn()
  }
}

const onClickAwayFn = () => {
  closeFilters()
  activeItem.value = props.currentFilters[0].value
}

const timeframe = ref(null)
const properties = ref(null)
const status = ref(null)
const products = ref(null)

const scrollToElement = value => {
  if (value === 'timeframe')
    timeframe.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
  if (value === 'products')
    products.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
  if (value === 'properties')
    properties.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
  if (value === 'status') status.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
}

const hasChanges = computed(() => {
  return !isEqual(temporaryFiltersObject.value, initialFilters.value)
})

const changes = computed(() => {
  const changedFilters = {}

  Object.keys(temporaryFiltersObject.value).forEach(key => {
    if (!isEqual(temporaryFiltersObject.value[key], initialFilters.value[key])) {
      changedFilters[key] = temporaryFiltersObject.value[key]
    }
  })

  return changedFilters
})

const onSelectTempTimeframe = newTimeframe => {
  temporaryFiltersObject.value['timeframe'] = newTimeframe
}
const onSelectTempCustomStartDate = newStartDate => {
  temporaryFiltersObject.value['startDate'] = newStartDate
}
const onSelectTempCustomEndDate = newEndDate => {
  temporaryFiltersObject.value['endDate'] = newEndDate
}

const onSelectTempProducts = product => {
  const isAll = product.id === 0
  const hasAll = temporaryFiltersObject.value['products'].some(p => p.id === 0)

  if (isAll) {
    if (
      !temporaryFiltersObject.value['products'].find(p => p.id === 0) ||
      temporaryFiltersObject.value['products'].length > 1
    ) {
      temporaryFiltersObject.value['products'] = [product]
    }
    return
  }

  let newSelection
  if (hasAll) {
    newSelection = [product]
  } else {
    const exists = temporaryFiltersObject.value['products'].find(p => p.id === product.id)
    if (exists) {
      newSelection = temporaryFiltersObject.value['products'].filter(p => p.id !== product.id)
      if (newSelection.length === 0) return
    } else {
      newSelection = [...temporaryFiltersObject.value['products'], product]
    }
  }

  temporaryFiltersObject.value['products'] = newSelection
}

const onSelectTempStatus = status => {
  const isAllOption = status.id === 0 || status.value === 'all'
  const hasAll = temporaryFiltersObject.value['status'].some(item => item.id === 0 || item.value === 'all')
  const exists = temporaryFiltersObject.value['status'].some(item => item.id === status.id)

  const allStatusOptions = props.statusOptions || []
  const totalRegularOptions = allStatusOptions.filter(opt => opt.id !== 0).length
  const allOption = allStatusOptions.find(opt => opt.id === 0)

  if (isAllOption) {
    if (!exists) {
      temporaryFiltersObject.value['status'] = [status]
    } else if (temporaryFiltersObject.value['status'].length === 1) {
      return
    }
    return
  }

  let newSelection

  if (hasAll) {
    newSelection = [status]
  } else if (exists) {
    newSelection = temporaryFiltersObject.value['status'].filter(item => item.id !== status.id)
    if (newSelection.length === 0) return
  } else {
    newSelection = [...temporaryFiltersObject.value['status'], status]
  }

  const regularSelectedCount = newSelection.filter(item => item.id !== 0).length

  if (regularSelectedCount === totalRegularOptions) {
    temporaryFiltersObject.value['status'] = [allOption]
  } else {
    temporaryFiltersObject.value['status'] = newSelection
  }
}

const onSelectTempPropertyFilters = newValue => {
  const isAllOption = newValue.id === 0
  const currentHasAll = temporaryFiltersObject.value['properties'].some(item => item.id === 0)

  if (isAllOption) {
    temporaryFiltersObject.value['properties'] = [newValue]
  } else {
    if (currentHasAll) {
      temporaryFiltersObject.value['properties'] = temporaryFiltersObject.value['properties'].filter(
        item => item.id !== 0
      )
    }

    const index = temporaryFiltersObject.value['properties'].findIndex(item => item.id === newValue.id)
    if (index > -1) {
      temporaryFiltersObject.value['properties'].splice(index, 1)

      if (temporaryFiltersObject.value['properties'].length === 0) {
        temporaryFiltersObject.value['properties'] = [
          { value: 'all', label: 'campaign_subscription_status_all', id: 0 },
        ]
      }
    } else {
      temporaryFiltersObject.value['properties'].push(newValue)
    }
  }
}
</script>
